import type { ComponentProps } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../Input2';
import { cn } from '../cn';
import { ESC_PRIORITY, useEscStack } from '../contexts/EscCancelable.context';
import usePortal from '../hooks/usePortal';
import { General } from '../StrokeIcons';

const PopupPortal = ({ id, children }: ComponentProps<'div'>) => {
  const target = usePortal(id || 'poseidon-modal-portal');
  return createPortal(children, target);
};

export type PopupProps = ComponentProps<'div'> & {
  onClose: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  backdropClassName?: string;
};

export default function Popup({ onClose, children, className, backdropClassName }: PopupProps) {
  useEscStack('POPUP2', ESC_PRIORITY.INTERSTITIAL, onClose);

  React.useEffect(() => {
    document.body.classList.add('popupShown');
    return () => {
      document.body.classList.remove('popupShown');
    };
  }, []);

  return (
    <PopupPortal>
      <div className="fixed left-0 top-0 z-9998 flex h-screen w-screen flex-col items-center justify-center">
        <AnimatePresence>
          <motion.div
            className={cn('absolute left-0 top-0 z-10 h-full w-full bg-black/80 backdrop-blur-sm', backdropClassName)}
            key="background"
            onClick={onClose}
            initial={{ opacity: 0.3 }}
            animate={{ opacity: 1 }}
          />
          <motion.div
            key="content"
            className={cn(
              'bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100',
              'relative z-10 my-2 mx-4 flex max-w-4xl flex-col items-stretch gap-4 rounded-lg p-5 text-center drop-shadow-lg',
              className,
            )}
            initial={{ scale: 0.99, y: '20px' }}
            animate={{ scale: 1, y: 0 }}>
            <div className="absolute right-1 top-1 -translate-y-1/2 translate-x-1/2">
              <Button
                className="rounded-full shadow-lg"
                size="sm"
                icon={<General.XMark />}
                onClick={onClose}
              />
            </div>
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    </PopupPortal>
  );
}
